import { ConnectionService } from '@app/shared/services/connection.service';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, Observable, concat, switchMap, tap, catchError, throwError } from 'rxjs';
import { AttributeValue } from '@app/shared/interfaces/Attribute';
import { Configurable, Product } from '../models/product';
import { AttributeIdOptionId, ProductTag } from '../models/ProductSubObjects';
import { Translation } from '@app/shared/interfaces/Translation';
import { ProductModelFields } from '../product-base-logic';
import {
  ConfigProductAttribute,
  ConfigurationProduct,
} from '../models/ConfigurationProduct';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ListPayload } from '../models/ListPayload';
import { GroupedProduct } from '../models/GroupedProduct';
import { AttributeTypes, HardCopy } from 'src/global-variables';
import { ObjectType } from '../models/Enums.enum';
import { FormArray, FormGroup } from '@angular/forms';
import { CommonService } from './common.service';
import { CustomAttribute } from '../models/AttributeDTO';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TreeCategory } from '../models/category';
import { ProductRelationship } from '../models/product-relationship';

@Injectable({ providedIn: 'root' })
export class ProductService {
  productNavProperties = [
    'Gtin',
    'MapProducts',
    'ProductAttributes',
    'ProductCategories',
    'ProductPrices',
    'ProductTags',
    'ConfigurableProductProducts(expand=ConfigProductAttributes)',
  ];
  expandStatement = this.productNavProperties.join(',');
  customProductUrl = `${AppConfigService.settings.apiUrl}CustomProduct/`;
  private readonly apiUrl = AppConfigService.settings.apiUrl;

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private notification: NzNotificationService
  ) {}

  getProductsList(
    payload: ListPayload
  ): Observable<{ records: Product[]; recordsCount: number }> {
    return this.http.post<{ records: Product[]; recordsCount: number }>(
      this.customProductUrl + `ProductsList`,
      payload
    );
  }

  selectAllProducts(payload: ListPayload): Observable<number[]> {
    return this.http.post<number[]>(
      `${this.customProductUrl}select-all-products`,
      payload
    );
  }
  getProduct(
    id: number,
    languageId: number = 0
  ): Observable<{ product: Product; completion: any }> {
    // if (languageId != 0) {
    const productFields = ProductModelFields;
    return this.http.get<{ product: Product; completion: any }>(
      this.customProductUrl + `GetProductDetails/${id}/${languageId}`
    );
  }

  getProductHeader(
    id: number,
    languageId: number = 0
  ): Observable<{ product: Product; completion: any }> {
    // if (languageId != 0) {
    const productFields = ProductModelFields;
    return this.http.get<{ product: Product; completion: any }>(
      AppConfigService.settings.dataApiUrl + `Product/${id}`
    );
  }

  getProductBySku(sku: string): Observable<Product | null> {
    if (sku == null || sku == '') {
      return of(null);
    }
    return this.http.get<Product | null>(
      this.customProductUrl + `GetProductBySku?sku=${encodeURI(sku)}`
    );
  }
  postProduct(product: any) {
    return this.http.post(
      `${AppConfigService.settings.dataApiUrl}Product`,
      product
    );
  }
  updateProdcut(product: any, id: number) {
    return this.http.put(
      `${AppConfigService.settings.dataApiUrl}Product(${id})`,
      product
    );
  }
  deleteProduct(productId: number) {
    return this.http.delete(
      `${AppConfigService.settings.dataApiUrl}Product(${productId})`
    );
  }
  postProductGroups(products: GroupedProduct[], productId: number) {
    return this.http.post(
      this.customProductUrl + `SaveProductGroup/${productId}`,
      products
    );
  }
  getGroupProducts(id: number): Observable<GroupedProduct[]> {
    return this.http.get<GroupedProduct[]>(
      this.customProductUrl + `GetGroupProducts/${id}`
    );
  }
  postConfigurationProducts(configProducts: ConfigurationProduct[]) {
    return this.http.post(
      this.customProductUrl + `PostConfigurationProducts`,
      configProducts
    );
  }
  updateConfigurationProducts(configProducts: ConfigurationProduct[]) {
    return this.http.put(
      this.customProductUrl + `UpdateConfigurationProducts`,
      configProducts
    );
  }

  getCategories(): Observable<TreeCategory[]> {
    return this.http.get<TreeCategory[]>(
      `${this.apiUrl}CustomCategory/categories-tree`
    );
  }

  getConfigurationProducts(ids: number[]): Observable<Product[]> {
    return this.http.post<Product[]>(
      this.customProductUrl + `GetProductsFromAttributes`,
      ids
    );
  }

  getVariantProducts(parentId: number, payload: AttributeIdOptionId[]) {
    return this.http.post<Product[]>(
      `${this.customProductUrl}GenerateVariants/${parentId}`,
      payload
    );
  }

  submitProduct(
   
    formGroup: FormGroup,
    selectedId: number,
    languageId: number,
    baseProduct: Product,
    selectedCategories: number[],
    selectedProductTags: number[],
    configurationProducts: { products: number[]; attributes: number[] },
    variantProducts: Product[]
  ) {
    delete baseProduct.updatedBy;
    const product = HardCopy(formGroup.value);
    product.id = selectedId;
    baseProduct.name = product.name;
    //#region Product Attributes
    const productAttributes = product.attributeValues;
    let handledAttributes: AttributeValue[] = [];

    if (variantProducts != null && variantProducts.length > 0) {
      variantProducts.forEach((variant) => {
        delete variant.createdAt;
        delete variant.updatedAt;
        delete variant.parents;
        delete variant.variants;
        var attValues = variant.attributeValues;
        if (attValues != null) {
          attValues.forEach((value) => {
            value.objectType = 12;
            Object.keys(value).forEach((key) => {
              if (value[key] == null) {
                delete value[key];
              }
            });
          });
        }
      });
      product.variants = variantProducts;
      baseProduct.variants = variantProducts;
    }

    const attributes = this.commonService.helperObject[
      'attributes'
    ] as CustomAttribute[];
    if (Object.keys(productAttributes).length > 0) {
      Object.keys(productAttributes).forEach((attId) => {
        const attribute = attributes.find((x) => x.id == +attId);
        if (productAttributes[attId] != null && attribute) {
          if (
            [
              AttributeTypes.Picture,
              AttributeTypes.MediaGallery,
              AttributeTypes.Video,
            ].includes(attribute.type)
          ) {
            return;
          }
          if (attribute?.type == 12) {
            productAttributes[attId] =
              productAttributes[attId] === 'Y' ||
              productAttributes[attId] === true
                ? 'Y'
                : 'N';
          }
          if (
            productAttributes[attId] == null ||
            productAttributes[attId] === ''
          ) {
            return;
          }
          handledAttributes.push({
            AttributeId: +attId,
            Value: productAttributes[attId].toString(),
            LanguageId: languageId ?? 0,
            ObjectType: 12,
            OptionValueId:
              typeof productAttributes[attId] == 'number' &&
              [AttributeTypes.Select].indexOf(attribute.type!) !== -1
                ? productAttributes[attId]
                : undefined,
          });
        }
      });
    }
    product.attributeValues = handledAttributes;
    baseProduct.attributeValues = handledAttributes;
  
    //#endregion
    //#region Product Categories
    if (selectedCategories.length > 0) {
      selectedCategories = Array.from(new Set<number>(selectedCategories));
      baseProduct.productCategories = selectedCategories.map((x) => {
        return { categoryId: x, productId: 0 };
      });
    } else {
      baseProduct.productCategories = [];
    }
    //#endregion
    //#region Product Tags
    baseProduct.productTags = selectedProductTags.map((tag) => {
      const productTag: ProductTag = {
        tagId: tag,
      };
      return productTag;
    });
    //#endregion

    //#region Product Kit
    delete product.productKitParents;
    delete baseProduct.productKitParents;
    delete product.channelMappingData;
    delete baseProduct.channelMappingData;
    const productKit = formGroup.controls['productKitParents'] as FormArray;
    if (productKit.controls.length > 0) {
      baseProduct.productKitParents = productKit.controls.map((x) => x.value);
      baseProduct.productKitParents.map(
        (x) => (x.parentId = baseProduct.id ?? 0)
      );
    }
    //#endregion
    //#region deleting unwanted properties from product's object
    delete baseProduct.completionSet;
    delete baseProduct.createdAt;
    delete baseProduct.updatedAt;

    //#endregion
    const productRelationshipProducts = (
      product.productRelationshipProducts as ProductRelationship[]
    ).reduce((acc: ProductRelationship[], cur: ProductRelationship) => {
      const exists = acc.some(
        (x) => x.objectId === cur.objectId && x.relationId === cur.relationId
      );
      if (!exists) {
        acc.push(cur);
      }
      return acc;
    }, []);
    baseProduct.productRelationshipProducts = productRelationshipProducts;
    delete baseProduct.inverseInheritFromNavigation;
    let observable = new Observable();
    switch (baseProduct.type?.toLowerCase()) {
      case 'variant':
      case 'simple':
        observable = this.updateProdcut(baseProduct, selectedId);
        break;
      case 'configurable':
        // if (configurationProducts.products.length == 0) {
        //   this.notification.error(
        //     'No chosen products',
        //     'Please choose at least one configuration product'
        //   );
        //   break;
        // }
        // observable = this.updateProdcut(baseProduct, selectedId).pipe(
        //   switchMap((product: any) => {
        //     let configProducts: ConfigurationProduct[] = [];
        //     if (configurationProducts.products.length > 0) {
        //       configProducts = configurationProducts.products.map((prod) => {
        //         return {
        //           childProductId: prod,
        //           productId: product.id ?? product.Id,
        //           configProductAttributes: configurationProducts.attributes.map(
        //             (att) => {
        //               const attribute: ConfigProductAttribute = {
        //                 attributeId: att,
        //                 productId: product.id ?? product.Id,
        //               };
        //               return attribute;
        //             }
        //           ),
        //         };
        //       });
        //     }
        //     return this.updateConfigurationProducts(configProducts);
        //   })
        // );
        // delete product.configurableProductProducts;
        // delete baseProduct.configurableProductProducts;
        // break;
      case 'grouped':
     
      
        baseProduct.groupedProductBaseProducts =
          product.groupedProductBaseProducts;
          
          if (!baseProduct.groupedProductBaseProducts) {
            baseProduct.groupedProductBaseProducts = [];
          }
        // if (
        //   baseProduct.groupedProductBaseProducts == null ||
        //   baseProduct.groupedProductBaseProducts.length == 0
        // ) {
        //   this.notification.error(
        //     'No chosen products',
        //     'Please choose at least one product in grouped product section'
        //   );
        //   return;
        // }
        observable = this.updateProdcut(baseProduct, selectedId).pipe(
          switchMap((product: any) => {
            
            const productGroup: GroupedProduct[] =
              baseProduct.groupedProductBaseProducts!.filter(
                (x) => x.position != null && x.defaultQuantity != null
              );
        
            productGroup.forEach(
              (prod) => {prod.baseProductId = product.id ?? product.Id
              }
            );
            return this.postProductGroups(productGroup, baseProduct.id!);
          })
        );
        break;
      default:
        // this.notification.error('Not implemented type', '');
        break;
    }
    // Object.assign(baseProduct, product);
    delete (baseProduct as any).completion;
    delete baseProduct.productRelationshipObjects;
    delete baseProduct.attributeDisplayProperties;
    baseProduct = { ...baseProduct, ...product };
    return observable;
  }
  submitTranslations(
    formGroup: FormGroup,
    languageId: number,
    selectedId: number,
    product: Product
  ) {
    const productValues = formGroup.value;
    product.attributeValues = productValues.attributeValues;
    const productFields = Object.keys(product).filter(
      (x) =>
        typeof productValues[x] === 'string' ||
        typeof productValues[x] === 'number'
    );
    const productTranslations: Translation[] = productFields.map((field) => {
      return {
        Label: productValues[field].toString(),
        LanguageId: languageId,
        ObjectId: selectedId,
        ObjectType: ObjectType.Product,
        ObjectField: field,
      };
    });
    let attributeTranslations: AttributeValue[] = [];
    if (productValues.attributeValues != null) {
      const AttributeValues = productValues.attributeValues;
      attributeTranslations = Object.keys(AttributeValues)
        .filter((x) => AttributeValues[x] != null)
        .map((att) => {
          const attribute = this.commonService.helperObject['attributes'].find(
            (x: any) => x.Id === +att || x.id === +att
          );
          let value = AttributeValues[att]?.toString();
          if (
            value != null &&
            attribute != null &&
            (attribute.Type?.toString() === AttributeTypes.Date.toString() ||
              attribute.type.toString() === AttributeTypes.Date.toString())
          ) {
            value = JSON.stringify(AttributeValues[att]);
          }
          if (attribute?.Type === 12 || attribute?.type === 12) {
            value = value === 'Y' || value === true ? 'Y' : 'N';
          }
          const attributes: AttributeValue = {
            AttributeId: +att,
            LanguageId: languageId,
            Value: value,
            ObjectId: selectedId,
            ObjectType: 12,
          };
          return attributes;
        });
    }
    const attributeTranslations$ =
      this.commonService.saveProductAttributesTransaltions(
        attributeTranslations
      );
    const productTranslations$ =
      this.commonService.saveTranslations(productTranslations);

    return concat(attributeTranslations$, productTranslations$);
  }

  getParentAttributeValues(productId: number) {
    return this.http.get(
      `${this.apiUrl}CustomAttribute/ParentAttributeValues/${productId}`
    );
  }

  getProductsToInherit(
    productId: number,
    payload: ListPayload
  ): Observable<Product[]> {
    return this.http.post<Product[]>(
      `${this.apiUrl}CustomProduct/products-to-inherit/${productId}`,
      payload
    );
  }

  exportProducts(ids: number[]) {
    return this.http
      .post(`${this.apiUrl}CustomProduct/export-products`, ids, {
        responseType: 'blob',
      })
      .pipe(
        tap((response) => {
        
          const blob = new Blob([response], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = 'exported-products.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
      );
  }

  removeProductRelation(
    baseId: number,
    relatedId: number,
    relationshipId: number
  ) {
    return this.http.put(
      `${this.apiUrl}CustomProduct/RemoveProductRelation?baseId=${baseId}&relatedId=${relatedId}&relationshipId=${relationshipId}`,
      null
    );
  }

  
  getConfigurableAttributes(attributeSetId: number): Observable<Configurable[]> {
                      
    return this.http.get<Configurable[]>(`${this.apiUrl}CustomProduct/ConfigurableAttributes?attributeSetId=${attributeSetId}`);
  }

  bulkDeleteProduct(productIds: Set<number>): Observable<any> {
    const url = `${AppConfigService.settings.dataApiUrl}Product/bulkdelete`;
    const productIdsArray = Array.from(productIds); 
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: productIdsArray  
    };
    return this.http.delete(url, options).pipe(
      catchError((error) => {
 
        
        return throwError(() => new Error(error.message || 'Failed to delete products'));
      })
    );
  }

  getChangeLog(): Observable<any> {

    return this.http.get(`${AppConfigService.settings.dataApiUrl}ChangeLog`);

  }
 
  getChangeLogByKey(
    productId: number,
    objectType: number,
   
  ): Observable<any> {
    const url = `${AppConfigService.settings.dataApiUrl}ChangeLog/GetObjectChangeLogs/${productId}/${objectType}`;
    return this.http.get<any>(
      url,

      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }
  
  
  getCustomProductById(productId: number): Observable<any> {
    const url = `${AppConfigService.settings.baseUrl}api/CustomProduct/${productId}`;
    return this.http.get<any>(url);
  }
}
